import { inject, Injectable } from '@angular/core';
import { APP_CONFIG_TOKEN } from '@quipex/core/config';
import {
    normalizeToProblemDetails,
    type ResolvedProblemDetails,
} from '@quipex/core/utils';
import { GOOGLE_DRIVE_AUTH_ERROR } from '../error/error-codes';
import { type BuildUrnParts, injectError } from '../error/inject-error';

const GOOGLE_IDENTITY_SCRIPT_URL = 'https://accounts.google.com/gsi/client';
const GOOGLE_DRIVE_SCOPES = ['https://www.googleapis.com/auth/drive.readonly'];

interface GoogleIdentityTokenClient {
    callback: (response: GoogleIdentityTokenResponse) => void;
    error_callback?: (error: GoogleIdentityTokenError) => void;
    requestAccessToken: (options?: { prompt?: string }) => void;
}

interface GoogleIdentityTokenResponse {
    access_token?: string;
    expires_in?: number;
    error?: string;
    error_description?: string;
}

interface GoogleIdentityTokenError {
    type?: string;
    message?: string;
}

interface GoogleDriveAccessToken {
    access_token: string;
    expires_in?: number;
}

type GoogleDriveAuthErrorSpec =
    (typeof GOOGLE_DRIVE_AUTH_ERROR)[keyof typeof GOOGLE_DRIVE_AUTH_ERROR];

interface GoogleIdentityApi {
    accounts: {
        oauth2: {
            initTokenClient: (config: {
                client_id: string;
                scope: string;
                callback: (response: GoogleIdentityTokenResponse) => void;
                error_callback?: (error: GoogleIdentityTokenError) => void;
            }) => GoogleIdentityTokenClient;
        };
    };
}

type WindowWithGoogleIdentity = Window &
    typeof globalThis & {
        google?: GoogleIdentityApi;
    };

@Injectable({
    providedIn: 'root',
})
export class GoogleDriveAuthService {
    //#region Dependency injection
    private readonly appConfig = inject(APP_CONFIG_TOKEN);
    private readonly clientError = injectError();
    //#endregion

    //#region Constants
    private readonly loggingId: BuildUrnParts = [
        'service',
        'google-drive-auth',
    ];
    //#endregion

    //#region State
    private accessToken: string | null = null;
    private accessTokenExpiresAtMs: number | null = null;
    private scriptLoadPromise: Promise<void> | null = null;
    private tokenClient: GoogleIdentityTokenClient | null = null;
    //#endregion

    //#region Public methods
    public hasToken(): boolean {
        return (
            !!this.accessToken &&
            !!this.accessTokenExpiresAtMs &&
            Date.now() < this.accessTokenExpiresAtMs
        );
    }

    public getToken(): string | null {
        if (!this.hasToken()) {
            this.clearToken();
            return null;
        }

        return this.accessToken;
    }

    public async connect(): Promise<string> {
        const cachedToken = this.getToken();
        if (cachedToken) return cachedToken;

        const client = await this.getTokenClient();
        const token = await this.requestAccessToken(client);
        this.accessToken = token.access_token;
        this.accessTokenExpiresAtMs =
            Date.now() + (token.expires_in ?? 3600) * 1000;
        return this.accessToken;
    }
    //#endregion

    //#region Private methods
    private async getTokenClient(): Promise<GoogleIdentityTokenClient> {
        if (this.tokenClient) {
            return this.tokenClient;
        }

        const clientId = this.appConfig.googleClientId?.trim();
        if (!clientId) {
            throw this.createGoogleDriveError(
                GOOGLE_DRIVE_AUTH_ERROR.ConfigurationInvalid,
                'Google Drive is not configured',
                'Google Drive browser access is not configured for this environment.'
            );
        }

        await this.loadGoogleIdentityScript();
        const google = this.getGoogleIdentity();

        this.tokenClient = google.accounts.oauth2.initTokenClient({
            client_id: clientId,
            scope: GOOGLE_DRIVE_SCOPES.join(' '),
            callback: () => undefined,
        });

        return this.tokenClient;
    }

    private requestAccessToken(
        client: GoogleIdentityTokenClient
    ): Promise<GoogleDriveAccessToken> {
        return new Promise((resolve, reject) => {
            client.callback = (response) => {
                if (response.error || !response.access_token) {
                    reject(
                        this.createGoogleDriveError(
                            GOOGLE_DRIVE_AUTH_ERROR.AuthorizationFailed,
                            'Google Drive connection failed',
                            response.error_description ??
                                response.error ??
                                'Google Drive did not return an access token.'
                        )
                    );
                    return;
                }

                resolve({
                    access_token: response.access_token,
                    expires_in: response.expires_in,
                });
            };
            client.error_callback = (error) => {
                reject(
                    this.createGoogleDriveError(
                        GOOGLE_DRIVE_AUTH_ERROR.TokenRequestFailed,
                        'Google Drive connection failed',
                        error.message ??
                            error.type ??
                            'Google Drive token request failed.'
                    )
                );
            };

            try {
                client.requestAccessToken({
                    prompt: 'consent',
                });
            } catch (error) {
                reject(
                    this.createGoogleDriveError(
                        GOOGLE_DRIVE_AUTH_ERROR.PopupBlocked,
                        'Google Drive connection blocked',
                        error instanceof Error
                            ? error.message
                            : 'The Google Drive connection window was blocked by the browser.',
                        undefined,
                        [error]
                    )
                );
            }
        });
    }

    private loadGoogleIdentityScript(): Promise<void> {
        if (this.getGoogleIdentityOrNull()) {
            return Promise.resolve();
        }

        if (this.scriptLoadPromise) {
            return this.scriptLoadPromise;
        }

        this.scriptLoadPromise = new Promise((resolve, reject) => {
            const existingScript = document.querySelector<HTMLScriptElement>(
                `script[src="${GOOGLE_IDENTITY_SCRIPT_URL}"]`
            );
            const script = existingScript ?? document.createElement('script');

            script.addEventListener('load', () => resolve(), { once: true });
            script.addEventListener(
                'error',
                () => {
                    this.scriptLoadPromise = null;
                    reject(
                        this.createGoogleDriveError(
                            GOOGLE_DRIVE_AUTH_ERROR.ConfigurationInvalid,
                            'Google Drive is not available',
                            'The Google Identity Services script failed to load.'
                        )
                    );
                },
                { once: true }
            );

            if (!existingScript) {
                script.src = GOOGLE_IDENTITY_SCRIPT_URL;
                script.async = true;
                script.defer = true;
                document.head.append(script);
            }
        });

        return this.scriptLoadPromise;
    }

    private getGoogleIdentity(): GoogleIdentityApi {
        const google = this.getGoogleIdentityOrNull();
        if (!google) {
            throw this.createGoogleDriveError(
                GOOGLE_DRIVE_AUTH_ERROR.ConfigurationInvalid,
                'Google Drive is not available',
                'Google Identity Services did not initialize.'
            );
        }

        return google;
    }

    private getGoogleIdentityOrNull(): GoogleIdentityApi | null {
        return (window as WindowWithGoogleIdentity).google ?? null;
    }

    private clearToken(): void {
        this.accessToken = null;
        this.accessTokenExpiresAtMs = null;
    }

    private createGoogleDriveError(
        spec: GoogleDriveAuthErrorSpec,
        title: string,
        detail: string,
        context?: Record<string, unknown>,
        consoleErrorRestArgs?: readonly unknown[]
    ): ResolvedProblemDetails {
        const error = this.clientError.create(spec, {
            urnParts: [...this.loggingId, spec.statusText] as BuildUrnParts,
            context,
            consoleErrorRestArgs,
        });

        return {
            ...normalizeToProblemDetails(error),
            title,
            detail,
        };
    }
    //#endregion
}
